/**
 * src/components/layout/Sidebar.jsx
 * Barra lateral de navegación — tres territorios.
 *
 * El menú era una lista plana de diez entradas para catorce páginas, así que
 * dos (`/familias` y `/scheduler`) no estaban en ninguna parte: solo se llegaba
 * desde un KPI del Dashboard. Ya había pasado con `Actividad` (c3af162). Una
 * lista plana no tiene dónde meter una página nueva, y por eso las páginas
 * nuevas se quedaban fuera.
 *
 * Agrupadas por la pregunta que responden, la lista se lee de un vistazo y cada
 * página tiene un sitio donde caber. `tests/test_navegacion_panel.py` comprueba
 * que ninguna ruta del panel se quede otra vez sin puerta.
 */
import { NavLink, useNavigate } from 'react-router-dom'
import { useAuthStore } from '../../store/authStore'

// Los tres territorios, en el orden en que se usan: primero se comprueba que la
// máquina corrió, luego se atiende a un cliente, luego se mira el negocio.
export const TERRITORIOS = [
  { nombre: 'Vigilar',  pregunta: '¿corrió la máquina?' },
  { nombre: 'Clientes', pregunta: '¿qué pasa con uno?' },
  { nombre: 'Negocio',  pregunta: '¿entra alguien y paga?' },
]

export const NAV_ITEMS = [
  {
    to: '/', label: 'Estado', grupo: 'Vigilar', exact: true,
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <rect x="3" y="3" width="7" height="7" rx="1" />
        <rect x="14" y="3" width="7" height="7" rx="1" />
        <rect x="14" y="14" width="7" height="7" rx="1" />
        <rect x="3" y="14" width="7" height="7" rx="1" />
      </svg>
    ),
  },
  {
    to: '/clientes', label: 'Clientes', grupo: 'Clientes',
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
  },
  {
    to: '/ejecuciones', label: 'Ejecuciones', grupo: 'Vigilar',
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <polyline points="4 17 9 12 4 7" />
        <line x1="12" y1="19" x2="20" y2="19" />
      </svg>
    ),
  },
  {
    to: '/sla', label: 'SLA', grupo: 'Vigilar',
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
      </svg>
    ),
  },
  {
    to: '/embudo', label: 'Embudo', grupo: 'Negocio',
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <path d="M3 4h18l-7 8v6l-4 2v-8L3 4z" />
      </svg>
    ),
  },
  {
    to: '/alertas', label: 'Alertas', grupo: 'Vigilar',
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
        <path d="M13.73 21a2 2 0 0 1-3.46 0" />
      </svg>
    ),
  },
  {
    // Entre Embudo y Prospección por decisión de Manuel (27-08): salió del
    // fondo de Estado a página propia.
    to: '/cifras-landing', label: 'Cifras landing', grupo: 'Negocio',
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <circle cx="12" cy="12" r="10" />
        <path d="M9 12l2 2 4-4" />
      </svg>
    ),
  },
  {
    to: '/prospeccion', label: 'Prospección', grupo: 'Negocio',
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <circle cx="11" cy="11" r="8" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
      </svg>
    ),
  },
  {
    // Radar de Compra Ágil (Manuel, 30-10-2026, con turno sobre este fichero
    // compartido). En «Negocio» y no en «Vigilar» a propósito: no dice si una
    // máquina corrió —eso es la tabla de noches, que esta pantalla NO trae—,
    // dice qué oportunidades le encontró el radar a cada tenant.
    to: '/radar-ca', label: 'Radar CA', grupo: 'Negocio',
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <circle cx="12" cy="12" r="9" />
        <circle cx="12" cy="12" r="4" />
        <line x1="12" y1="3" x2="12" y2="21" />
        <line x1="3" y1="12" x2="21" y2="12" />
      </svg>
    ),
  },
  {
    to: '/uso', label: 'Uso y churn', grupo: 'Negocio',
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
      </svg>
    ),
  },
  {
    // «Suscripciones» desde el 28-08: la página es la operación de cobros de
    // los tenants, no la facturación del usuario logueado (esa tarjeta se fue).
    to: '/settings/billing', label: 'Suscripciones', grupo: 'Negocio',
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
        <line x1="1" y1="10" x2="23" y2="10" />
      </svg>
    ),
  },
]

const ITEM_CUENTA = {
  to: '/settings/cuenta', label: 'Configuración',
  icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
      </svg>
    ),
}

function NavItem({ to, label, icon, exact }) {
  return (
    <NavLink
      to={to}
      end={exact}
      style={({ isActive }) => ({
        display: 'flex', alignItems: 'center', gap: 'var(--space-3)',
        padding: 'var(--space-2) var(--space-3)', borderRadius: 'var(--radius-sm)',
        textDecoration: 'none',
        fontSize: 'var(--text-base)', fontWeight: isActive ? 'var(--weight-medium)' : 'var(--weight-normal)',
        color: isActive ? 'var(--color-accent)' : 'var(--color-text-2)',
        background: isActive ? 'var(--color-accent-soft)' : 'transparent',
        border: isActive ? '1px solid var(--color-accent)' : '1px solid transparent',
        transition: 'all var(--t-base) var(--ease)',
      })}
      onMouseEnter={e => {
        const a = e.currentTarget
        if (!a.classList.contains('active'))
          a.style.background = 'var(--color-surface-2)'
      }}
      onMouseLeave={e => {
        const a = e.currentTarget
        if (!a.classList.contains('active'))
          a.style.background = 'transparent'
      }}
    >
      {icon}
      {label}
    </NavLink>
  )
}

// `activeJobs` admite tres valores y los tres significan cosas distintas:
//   número → jobs corriendo ahora   ·   0 → ninguno, y está medido
//   null   → no se pudo leer el planificador
export function Sidebar({ activeJobs = null, cargando = false }) {
  // «Scheduler inactivo» es una afirmación sobre la máquina. Solo se puede
  // decir habiendo leído; si no, se dice que no hay lectura.
  const sinLectura = !cargando && activeJobs === null
  const logout = useAuthStore(state => state.logout)
  const navigate = useNavigate()

  function handleLogout() {
    logout()
    navigate('/login', { replace: true })
  }

  return (
    <aside style={{
      width: '220px', flexShrink: 0,
      background: 'var(--color-surface)',
      borderRight: '1px solid var(--color-border)',
      display: 'flex', flexDirection: 'column',
      height: '100vh', position: 'sticky', top: 0,
    }}>
      {/* Logo */}
      <div style={{
        padding: 'var(--space-5) var(--space-4) var(--space-4)',
        borderBottom: '1px solid var(--color-border)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
          <div style={{
            width: '28px', height: '28px', borderRadius: 'var(--radius-sm)',
            background: 'var(--color-accent)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
            </svg>
          </div>
          <div>
            <div style={{
              fontSize: 'var(--text-md)', fontWeight: 'var(--weight-semibold)', color: 'var(--color-text)',
              fontFamily: 'var(--font-mono)', letterSpacing: '-0.02em',
            }}>
              CM Monitor
            </div>
            <div style={{
              fontSize: 'var(--text-micro)', color: 'var(--color-text-3)',
              letterSpacing: '0.06em', marginTop: '1px',
            }}>
              v3.0
            </div>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav style={{
        flex: 1, overflowY: 'auto',
        padding: 'var(--space-3) var(--space-2)',
        display: 'flex', flexDirection: 'column', gap: 'var(--space-4)',
      }}>
        {TERRITORIOS.map(({ nombre, pregunta }) => {
          const items = NAV_ITEMS.filter(i => i.grupo === nombre)
          if (items.length === 0) return null
          return (
            <div key={nombre} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-05)' }}>
              <div
                title={pregunta}
                style={{
                  padding: '0 var(--space-3) var(--space-1)',
                  fontSize: 'var(--text-micro)', fontFamily: 'var(--font-mono)',
                  fontWeight: 'var(--weight-medium)', letterSpacing: '0.12em',
                  textTransform: 'uppercase', color: 'var(--color-text-3)',
                }}
              >
                {nombre}
              </div>
              {items.map(item => <NavItem key={item.to} {...item} />)}
            </div>
          )
        })}
      </nav>

      {/* Scheduler status indicator. Clicable (Manuel, 28-08, cierre de D2):
          el contador y la lista que resume —«Lo próximo» de Ejecuciones—
          quedan conectados en vez de ser texto muerto. */}
      <div style={{
        padding: 'var(--space-3) var(--space-4)',
        borderTop: '1px solid var(--color-border)',
      }}>
        <button
          type="button"
          onClick={() => navigate('/ejecuciones')}
          title="Ver la línea de tiempo de ejecuciones"
          style={{
            display: 'flex', alignItems: 'center', gap: 'var(--space-2)',
            padding: 'var(--space-2) var(--space-3)', borderRadius: 'var(--radius-sm)',
            background: 'var(--color-surface-2)',
            border: '1px solid var(--color-border)',
            width: '100%', textAlign: 'left', cursor: 'pointer',
            fontFamily: 'var(--font-ui)',
            transition: 'border-color var(--t-base) var(--ease)',
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--color-border-2)' }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--color-border)' }}
        >
          <span style={{
            width: '7px', height: '7px', borderRadius: 'var(--radius-pill)', flexShrink: 0,
            background: sinLectura ? 'var(--color-warn)'
              : activeJobs > 0 ? 'var(--color-ok)' : 'var(--color-text-3)',
            ...(activeJobs > 0 ? { animation: 'pulseDot 1.4s ease-in-out infinite' } : {}),
          }} />
          <div>
            <div style={{
              fontSize: 'var(--text-xs)',
              color: sinLectura ? 'var(--color-warn)' : 'var(--color-text-2)',
              fontFamily: 'var(--font-mono)',
            }}>
              {cargando ? 'Leyendo el planificador…'
                : sinLectura ? 'Planificador sin lectura'
                : activeJobs > 0
                  ? `${activeJobs} job${activeJobs > 1 ? 's' : ''} activo${activeJobs > 1 ? 's' : ''}`
                  : 'Scheduler inactivo'}
            </div>
          </div>
        </button>
      </div>

      {/* Cuenta: no es un territorio de trabajo, es tuya. Por eso va al pie,
          junto a cerrar sesión, y no compitiendo con las pantallas del día. */}
      <div style={{
        padding: 'var(--space-2) var(--space-2) var(--space-4)',
        display: 'flex', flexDirection: 'column', gap: 'var(--space-05)',
      }}>
        <NavItem {...ITEM_CUENTA} />
        <button
          onClick={handleLogout}
          style={{
            width: '100%', display: 'flex', alignItems: 'center', gap: 'var(--space-2)',
            padding: 'var(--space-2) var(--space-3)', borderRadius: 'var(--radius-sm)',
            border: '1px solid transparent', background: 'transparent',
            color: 'var(--color-text-3)', cursor: 'pointer',
            fontSize: 'var(--text-base)', fontFamily: 'var(--font-ui)',
            transition: 'all var(--t-base) var(--ease)',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.background = 'var(--color-error-soft)'
            e.currentTarget.style.color = 'var(--color-error)'
          }}
          onMouseLeave={e => {
            e.currentTarget.style.background = 'transparent'
            e.currentTarget.style.color = 'var(--color-text-3)'
          }}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
            <polyline points="16 17 21 12 16 7" />
            <line x1="21" y1="12" x2="9" y2="12" />
          </svg>
          Cerrar sesión
        </button>
      </div>
    </aside>
  )
}
